import React, { useCallback } from 'react';
import { Box, Grid } from '@material-ui/core';
import { Container } from '../common/Container';
import { Loader } from '../common/Loader';
import { Typography } from '../common/Typography';
import { usePathFromHierarchy } from '../../hooks/usePathFromHierarchy';
import { Folder, RootFolder } from '../services/UploadManagementServiceTypes';
import { CreateFolder } from './CreateFolder';
import { ContentList } from './ContentList';
import { CreateMonoPanoramaGallery } from '../gallery/CreateMonoPanoramaGallery';
import { Breadcrumbs } from '../common/Breadcrumbs';
import { DataViewSwitch } from './DataViewSwitch';
import { createFolderUrl, createSharedFolderUrl } from './getContentUrl';
import { useUploadContext } from './UploadManagementContext';
import { convertUploadSort, UploadSortSelect, UploadSortValue } from './UploadSortSelect';

const UploadBreadcrumbs: React.FC<{ currentFolder: Folder | RootFolder }> = ({ currentFolder }) => {
  const { currentFolderHierarchy, view, shareGuid } = useUploadContext();
  const { breadcrumb } = usePathFromHierarchy({
    folderHierarchy: currentFolderHierarchy,
    content: currentFolder,
  });
  const makeLink = (guid: string) => {
    if (view === 'shared' && shareGuid) {
      return createSharedFolderUrl(`${shareGuid}/${guid}`);
    }
    return createFolderUrl(guid);
  };

  return (
    <Box marginBottom={5}>
      <Breadcrumbs breadcrumbData={breadcrumb} makeLink={makeLink} />
    </Box>
  );
};

const SortSelector: React.FC = () => {
  const { sortOrder, sortField, setSort } = useUploadContext();
  const onChange = useCallback(
    (value: UploadSortValue) => {
      setSort(convertUploadSort(value));
    },
    [setSort],
  );
  return <UploadSortSelect onChange={onChange} value={`${sortField}-${sortOrder}`} />;
};

export const UploadManagement: React.FunctionComponent = () => {
  const { requestStatus, view, currentFolder, getFolderListing, dataView, setDataView } =
    useUploadContext();

  if (requestStatus === 'success' && currentFolder) {
    return (
      <Container>
        <UploadBreadcrumbs currentFolder={currentFolder} />
        <Grid container justify='space-between' alignItems='flex-end'>
          <Grid item>
            {view === 'default' ? (
              <>
                <CreateFolder currentFolder={currentFolder} getFolderListing={getFolderListing} />
                <CreateMonoPanoramaGallery currentFolder={currentFolder} />
              </>
            ) : null}
          </Grid>
          <Grid item>
            <Grid container justify='space-between' alignItems='flex-end'>
              <Grid item>
                <Box marginRight={1}>
                  <SortSelector />
                </Box>
              </Grid>
              <Grid item>
                <DataViewSwitch dataView={dataView} setDataView={setDataView} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Box marginTop={3}>
          <ContentList currentFolder={currentFolder} />
        </Box>
      </Container>
    );
  }

  if (requestStatus === 'success' && !currentFolder) {
    return (
      <Container>
        <Typography variant='h1'>The Folder you are looking for cannot be found.</Typography>
      </Container>
    );
  }

  if (requestStatus === 'error') {
    return (
      <Container>
        <Typography variant='h1'>Error</Typography>
        <Typography>Folder could not be loaded!</Typography>
      </Container>
    );
  }
  return (
    <Container>
      <Loader />
    </Container>
  );
};
